import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPrayerTimes } from '../services/apiService';
import { findNextPrayer } from '../utils/prayerTimeUtils';
import { getLocationTimeFromTimezone, getEstimatedLocationTime } from '../utils/timezoneHelper';

/**
 * Custom hook to manage prayer times and related state.
 * Fetches today + tomorrow so countdowns always target the correct day.
 * Auto-refreshes when the local date changes (midnight crossover).
 *
 * @param {Object} coordinates - Latitude and longitude coordinates
 * @returns {Object} Prayer times data and related state
 */
const usePrayerTimes = (coordinates) => {
  const [prayerTimes, setPrayerTimes] = useState(null);         // today
  const [tomorrowPrayerTimes, setTomorrowPrayerTimes] = useState(null); // tomorrow
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [timeUntilNextPrayer, setTimeUntilNextPrayer] = useState('');

  // Preference for showing prayer markers on the timeline
  const [showPrayerMarkers, setShowPrayerMarkers] = useState(() => {
    const saved = localStorage.getItem('sunriseApp.showPrayerMarkers');
    return saved ? JSON.parse(saved) : false;
  });

  // Save prayer marker preference to localStorage
  useEffect(() => {
    localStorage.setItem('sunriseApp.showPrayerMarkers', JSON.stringify(showPrayerMarkers));
  }, [showPrayerMarkers]);

  // Track what we last loaded so we know when to re-fetch
  const loadedRef = useRef(null);   // { lat, lng, dateStr }
  const timerRef = useRef(null);
  const dailyCheckRef = useRef(null);

  // Core loader — fetches today + tomorrow
  const loadPrayerTimes = useCallback(async (coords, showSpinner = true) => {
    if (!coords || !coords.lat || !coords.lng) return;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = today.toISOString().slice(0, 10);

    // Skip if already loaded for same coords + date
    const ref = loadedRef.current;
    if (ref && ref.lat === coords.lat && ref.lng === coords.lng && ref.dateStr === dateStr) {
      return;
    }

    if (showSpinner) setLoading(true);
    setError(null);

    try {
      const [todayTimes, tomorrowTimes] = await Promise.all([
        fetchPrayerTimes(coords.lat, coords.lng, today),
        fetchPrayerTimes(coords.lat, coords.lng, tomorrow),
      ]);
      setPrayerTimes(todayTimes);
      setTomorrowPrayerTimes(tomorrowTimes);
      loadedRef.current = { lat: coords.lat, lng: coords.lng, dateStr };
    } catch (err) {
      console.error('Error loading prayer times:', err);
      setError(err.message || 'Failed to load prayer times');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on coordinate change
  useEffect(() => {
    loadPrayerTimes(coordinates, true);
  }, [coordinates, loadPrayerTimes]);

  // Daily refresh — check every 5 minutes if the date has changed
  useEffect(() => {
    dailyCheckRef.current = setInterval(() => {
      loadPrayerTimes(coordinates, false);
    }, 60 * 60 * 1000);

    return () => clearInterval(dailyCheckRef.current);
  }, [coordinates, loadPrayerTimes]);

  // Calculate the next prayer using timezone-aware time
  useEffect(() => {
    if (!prayerTimes) return;

    const updateNextPrayer = () => {
      // Use IANA timezone from API when available, fall back to estimated
      const locationTime = getLocationTimeFromTimezone(prayerTimes.timezone)
        || getEstimatedLocationTime(coordinates);

      const result = findNextPrayer(prayerTimes, locationTime);
      if (result) {
        setNextPrayer(result.prayer);
        setTimeUntilNextPrayer(result.formatted);
      } else {
        setNextPrayer(null);
        setTimeUntilNextPrayer('--');
      }
    };

    updateNextPrayer();
    timerRef.current = setInterval(updateNextPrayer, 60 * 1000);

    return () => clearInterval(timerRef.current);
  }, [prayerTimes, coordinates]);

  // Toggle prayer markers
  const togglePrayerMarkers = useCallback(() => {
    setShowPrayerMarkers(prev => !prev);
  }, []);

  return {
    prayerTimes,
    tomorrowPrayerTimes,
    loading,
    error,
    nextPrayer,
    timeUntilNextPrayer,
    showPrayerMarkers,
    setShowPrayerMarkers,
    togglePrayerMarkers
  };
};

export default usePrayerTimes;